import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Ban, Printer } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { SectionSpinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/Table';
import { toast } from '@/components/ui/Toast';
import { Can } from '@/app/guards/RoleGuard';
import { collectionsApi } from '@/services/endpoints/transactions';
import { queryKeys } from '@/services/queryKeys';
import { ApiError } from '@/services/apiClient';
import { ReceiptSlip } from '../components/ReceiptSlip';

export function CollectionDetailPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const receiptId = Number(id);

  const [isVoidOpen, setVoidOpen] = useState(false);
  const [reason, setReason] = useState('');

  const { data: receipt, isLoading } = useQuery({
    queryKey: queryKeys.collections.detail(receiptId),
    queryFn: () => collectionsApi.get(receiptId),
    enabled: Number.isFinite(receiptId),
  });

  const voidReceipt = useMutation({
    mutationFn: () => collectionsApi.void(receiptId, reason.trim()),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.collections.all });
      // Voiding reopens invoices, so their lists and the dashboard change too.
      void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.shops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      toast.success(t('collection.voided'));
      setVoidOpen(false);
      setReason('');
    },
    onError: (error) => toast.error(error instanceof ApiError ? error.message : t('errors.unknown')),
  });

  if (isLoading) return <SectionSpinner />;
  if (!receipt) return <EmptyState title={t('errors.notFound')} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title={`${t('collection.receiptNo')} ${receipt.receiptNo}`}
        description={receipt.shopName}
        className="no-print"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.print()}
              leftIcon={<Printer className="h-4 w-4" />}
            >
              {t('common.print')}
            </Button>
            {receipt.status === 'ACTIVE' && (
              <Can permission="collections:void">
                <Button
                  variant="danger"
                  onClick={() => setVoidOpen(true)}
                  leftIcon={<Ban className="h-4 w-4" />}
                >
                  {t('collection.void')}
                </Button>
              </Can>
            )}
          </div>
        }
      />

      {receipt.status === 'VOID' && (
        <Card className="border-red-200 bg-red-50 p-4 no-print">
          <div className="flex items-center gap-2">
            <Badge tone="danger">{t('collection.voidedBadge')}</Badge>
            <p className="text-sm text-red-800">{receipt.voidReason}</p>
          </div>
        </Card>
      )}

      <ReceiptSlip receipt={receipt} />

      <Modal
        isOpen={isVoidOpen}
        onClose={() => setVoidOpen(false)}
        title={t('collection.voidTitle')}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoidOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => voidReceipt.mutate()}
              isLoading={voidReceipt.isPending}
              disabled={reason.trim().length < 3}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {t('collection.voidWarning')}
          </p>
          <Input
            label={t('collection.voidReason')}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            required
            autoFocus
          />
        </div>
      </Modal>
    </div>
  );
}
